import { useState, useMemo } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import PawPrint from '../components/PawPrint';
import { Shield, Sparkles, ArrowLeft, Eye, EyeOff, Check, X } from 'lucide-react';
import api from '../lib/api';
import { toast } from 'sonner';
import { supabase } from '../lib/supabase';
import { useSafePostHog } from '../components/PostHogProvider';

const Signup = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({ email: '', password: '', full_name: '' });
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [passwordTouched, setPasswordTouched] = useState(false);

  // Real-time password validation
  const passwordChecks = useMemo(() => {
    const pwd = formData.password;
    return {
      minLength: pwd.length >= 8,
      hasNumber: /\d/.test(pwd),
      hasSpecial: /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?`~]/.test(pwd),
    };
  }, [formData.password]);

  const isPasswordValid = passwordChecks.minLength && passwordChecks.hasNumber && passwordChecks.hasSpecial;

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!isPasswordValid) {
      toast.error('Please fix the password requirements before signing up.');
      return;
    }

    setLoading(true);
    try {
      await api.post('/auth/signup', formData);
      toast.success('Account created! Check your email for OTP.');
      navigate('/confirm-email', { state: { email: formData.email } });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Signup failed');
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = async () => {
    sessionStorage.setItem('google_auth_start', Date.now().toString());
    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: window.location.origin + '/auth/callback'
      }
    });
    if (error) {
      sessionStorage.removeItem('google_auth_start');
      toast.error('Google signup failed: ' + error.message);
    }
  };

  const PasswordRule = ({ passed, label }) => (
    <div className={`flex items-center gap-1.5 text-[11px] font-semibold transition-colors ${passed ? 'text-emerald-600' : 'text-[#FF6B6B]'}`}>
      {passed ? <Check className="w-3 h-3" strokeWidth={3} /> : <X className="w-3 h-3" strokeWidth={3} />}
      {label}
    </div>
  );

  return (
    <div className="min-h-screen flex" data-testid="signup-page">
      <div className="hidden lg:flex lg:w-1/2 bg-[#1B4332] relative overflow-hidden flex-col justify-between p-12">
        <div className="absolute top-0 right-0 w-96 h-96 bg-[#BFDB38]/5 rounded-full -translate-y-1/2 translate-x-1/2"></div>
        <div className="absolute bottom-0 left-0 w-72 h-72 bg-[#FF6B6B]/5 rounded-full translate-y-1/2 -translate-x-1/2"></div>

        <div className="relative z-10">
          <Link to="/" className="flex items-center gap-2 mb-4">
            <PawPrint className="w-8 h-8" color="#BFDB38" />
            <span className="text-2xl font-black text-white" style={{ fontFamily: 'Nunito, sans-serif' }}>PurrCheck</span>
          </Link>
        </div>

        <div className="relative z-10 flex-1 flex flex-col justify-center">
          <h2 className="text-4xl xl:text-5xl font-black text-white tracking-tight leading-[1.1] mb-6" style={{ fontFamily: 'Nunito, sans-serif' }}>
            Join Thousands of Cat Parents
          </h2>
          <p className="text-base text-white/50 max-w-sm mb-10">
            Create your free account and start making smarter food choices for your feline friend.
          </p>
          <div className="space-y-4 max-w-sm">
            {[
              { icon: Shield, text: '3 free ingredient scans per month' },
              { icon: Sparkles, text: '5 free AI behavior questions' }
            ].map((item, i) => (
              <div key={i} className="flex items-center gap-3 bg-white/5 rounded-xl px-4 py-3">
                <item.icon className="w-5 h-5 text-[#BFDB38]" strokeWidth={2.5} />
                <span className="text-sm font-bold text-white/70">{item.text}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="relative z-10">
          <p className="text-xs text-white/30 uppercase tracking-wider font-bold">Free forever. Upgrade anytime.</p>
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center p-6 bg-[#FFFDF7]">
        <div className="w-full max-w-md">
          <div className="lg:hidden flex items-center gap-2 mb-8">
            <Link to="/" className="flex items-center gap-2">
              <PawPrint className="w-8 h-8" color="#BFDB38" />
              <span className="text-2xl font-black text-[#1B4332]" style={{ fontFamily: 'Nunito, sans-serif' }}>PurrCheck</span>
            </Link>
          </div>

          <Link to="/" className="inline-flex items-center gap-1.5 text-sm font-bold text-[#1B4332]/50 hover:text-[#1B4332] transition-colors mb-8">
            <ArrowLeft className="w-4 h-4" strokeWidth={2.5} /> Back to home
          </Link>

          <div data-testid="signup-form-card">
            <h1 className="text-3xl sm:text-4xl font-black text-[#1B4332] mb-2 tracking-tight" style={{ fontFamily: 'Nunito, sans-serif' }}>
              Create Account
            </h1>
            <p className="text-sm text-[#1B4332]/50 mb-8">Start checking your cat's food for free</p>

            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <Label htmlFor="full_name" className="text-[10px] uppercase tracking-[0.15em] font-bold text-[#FF6B6B]">Full Name</Label>
                <Input id="full_name" type="text" value={formData.full_name} onChange={(e) => setFormData({ ...formData, full_name: e.target.value })} className="bg-white border-2 border-[#1B4332]/10 rounded-xl focus:border-[#1B4332] h-12" placeholder="Jane Doe" required data-testid="signup-fullname-input" />
              </div>
              <div>
                <Label htmlFor="email" className="text-[10px] uppercase tracking-[0.15em] font-bold text-[#FF6B6B]">Email</Label>
                <Input id="email" type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} className="bg-white border-2 border-[#1B4332]/10 rounded-xl focus:border-[#1B4332] h-12" placeholder="you@example.com" required data-testid="signup-email-input" />
              </div>
              <div>
                <Label htmlFor="password" className="text-[10px] uppercase tracking-[0.15em] font-bold text-[#FF6B6B]">Password</Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    value={formData.password}
                    onChange={(e) => { setFormData({ ...formData, password: e.target.value }); if (!passwordTouched) setPasswordTouched(true); }}
                    className={`bg-white border-2 rounded-xl h-12 pr-12 transition-colors ${passwordTouched && !isPasswordValid ? 'border-[#FF6B6B]/50 focus:border-[#FF6B6B]' : 'border-[#1B4332]/10 focus:border-[#1B4332]'}`}
                    placeholder="Min 8 chars, 1 number, 1 special"
                    required
                    data-testid="signup-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 p-1 rounded-md text-[#1B4332]/30 hover:text-[#1B4332]/70 transition-colors"
                    tabIndex={-1}
                    data-testid="signup-password-toggle"
                  >
                    {showPassword ? <EyeOff className="w-4.5 h-4.5" strokeWidth={2} /> : <Eye className="w-4.5 h-4.5" strokeWidth={2} />}
                  </button>
                </div>

                {/* Real-time password validation rules */}
                {passwordTouched && (
                  <div className="mt-2 space-y-1 animate-in fade-in slide-in-from-top-1 duration-200" data-testid="password-validation-rules">
                    <PasswordRule passed={passwordChecks.minLength} label="At least 8 characters" />
                    <PasswordRule passed={passwordChecks.hasNumber} label="At least 1 number (0-9)" />
                    <PasswordRule passed={passwordChecks.hasSpecial} label="At least 1 special character (!@#$%...)" />
                  </div>
                )}
              </div>
              <Button
                type="submit"
                className="w-full rounded-full font-bold h-12 bg-[#1B4332] hover:bg-[#1B4332]/90 text-white text-base hover:-translate-y-0.5 transition-all disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:translate-y-0"
                disabled={loading || (passwordTouched && !isPasswordValid)}
                data-testid="signup-submit-btn"
              >
                {loading ? 'Creating Account...' : 'Create Account'}
              </Button>
            </form>

            <div className="relative my-6">
              <div className="absolute inset-0 flex items-center"><span className="w-full border-t-2 border-[#1B4332]/8"></span></div>
              <div className="relative flex justify-center text-[10px] uppercase tracking-wider"><span className="bg-[#FFFDF7] px-3 text-[#1B4332]/40 font-bold">Or continue with</span></div>
            </div>

            <Button type="button" variant="outline" onClick={handleGoogleLogin} className="w-full rounded-full font-bold h-12 border-2 border-[#1B4332]/10 hover:bg-[#1B4332]/5 text-[#1B4332]" data-testid="signup-google-btn">
              <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/></svg>
              Continue with Google
            </Button>

            <p className="text-center text-sm text-[#1B4332]/50 mt-8">
              Already have an account?{' '}
              <Link to="/login" className="text-[#FF6B6B] font-bold hover:underline" data-testid="signup-login-link">Sign in</Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Signup;
